/* Shared country + calling-code reference data. No canonical list existed
   anywhere in the codebase (checked frontend + backend) — this is standard,
   publicly-known reference data (country names / ISO dial codes), not
   user-specific content, introduced once here so every picker (Profile,
   Teacher Apply, Teacher search) can share it instead of re-typing lists. */
import { allCountries } from "country-region-data";

export const COUNTRIES = [
  "Afghanistan", "Algeria", "Australia", "Bahrain", "Bangladesh", "Belgium",
  "Brazil", "Brunei", "Canada", "China", "Egypt", "France", "Germany",
  "Ghana", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Italy", "Japan",
  "Jordan", "Kenya", "Kuwait", "Lebanon", "Libya", "Malaysia", "Maldives",
  "Mauritius", "Morocco", "Netherlands", "New Zealand", "Nigeria", "Norway",
  "Oman", "Pakistan", "Palestine", "Philippines", "Qatar", "Russia",
  "Saudi Arabia", "Senegal", "Singapore", "Somalia", "South Africa",
  "Spain", "Sri Lanka", "Sudan", "Sweden", "Switzerland", "Syria",
  "Tajikistan", "Tanzania", "Thailand", "Tunisia", "Turkey", "Uganda",
  "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Uzbekistan", "Yemen",
].sort();

/* Common languages spoken/taught by Quran students & teachers worldwide —
   standard reference data (not user-specific), used by TeacherApply's
   "Languages spoken" / "Teaching languages" pickers (C14). Not an
   exhaustive ISO 639 list — that would be hundreds of entries, too many to
   present as a chip-select; this covers the languages this app's actual
   userbase (per the country list above) is realistically drawn from. */
export const LANGUAGES = [
  "Arabic", "English", "Urdu", "Hindi", "Bengali", "Punjabi", "Pashto",
  "Persian/Farsi", "Turkish", "Malay", "Indonesian", "French", "German",
  "Spanish", "Portuguese", "Russian", "Swahili", "Somali", "Hausa",
  "Amharic", "Tamil", "Sinhala", "Mandarin Chinese", "Bosnian", "Albanian",
  "Kurdish", "Tajik", "Uzbek", "Wolof", "Dutch", "Italian",
].sort();

/* Country → calling code, for the WhatsApp number field. */
export const CALLING_CODES = [
  { country: "Afghanistan", code: "+93" }, { country: "Algeria", code: "+213" },
  { country: "Australia", code: "+61" }, { country: "Bahrain", code: "+973" },
  { country: "Bangladesh", code: "+880" }, { country: "Belgium", code: "+32" },
  { country: "Brazil", code: "+55" }, { country: "Brunei", code: "+673" },
  { country: "Canada", code: "+1" }, { country: "China", code: "+86" },
  { country: "Egypt", code: "+20" }, { country: "France", code: "+33" },
  { country: "Germany", code: "+49" }, { country: "Ghana", code: "+233" },
  { country: "India", code: "+91" }, { country: "Indonesia", code: "+62" },
  { country: "Iran", code: "+98" }, { country: "Iraq", code: "+964" },
  { country: "Ireland", code: "+353" }, { country: "Italy", code: "+39" },
  { country: "Japan", code: "+81" }, { country: "Jordan", code: "+962" },
  { country: "Kenya", code: "+254" }, { country: "Kuwait", code: "+965" },
  { country: "Lebanon", code: "+961" }, { country: "Libya", code: "+218" },
  { country: "Malaysia", code: "+60" }, { country: "Maldives", code: "+960" },
  { country: "Mauritius", code: "+230" }, { country: "Morocco", code: "+212" },
  { country: "Netherlands", code: "+31" }, { country: "New Zealand", code: "+64" },
  { country: "Nigeria", code: "+234" }, { country: "Norway", code: "+47" },
  { country: "Oman", code: "+968" }, { country: "Pakistan", code: "+92" },
  { country: "Palestine", code: "+970" }, { country: "Philippines", code: "+63" },
  { country: "Qatar", code: "+974" }, { country: "Russia", code: "+7" },
  { country: "Saudi Arabia", code: "+966" }, { country: "Senegal", code: "+221" },
  { country: "Singapore", code: "+65" }, { country: "Somalia", code: "+252" },
  { country: "South Africa", code: "+27" }, { country: "Spain", code: "+34" },
  { country: "Sri Lanka", code: "+94" }, { country: "Sudan", code: "+249" },
  { country: "Sweden", code: "+46" }, { country: "Switzerland", code: "+41" },
  { country: "Syria", code: "+963" }, { country: "Tajikistan", code: "+992" },
  { country: "Tanzania", code: "+255" }, { country: "Thailand", code: "+66" },
  { country: "Tunisia", code: "+216" }, { country: "Turkey", code: "+90" },
  { country: "Uganda", code: "+256" }, { country: "Ukraine", code: "+380" },
  { country: "United Arab Emirates", code: "+971" }, { country: "United Kingdom", code: "+44" },
  { country: "United States", code: "+1" }, { country: "Uzbekistan", code: "+998" },
  { country: "Yemen", code: "+967" },
].sort((a, b) => a.country.localeCompare(b.country));

/* Country → ISO 3166-1 alpha-2 code, for the State/Province dropdown (C7).
   Same 63-country set as COUNTRIES/CALLING_CODES above. Standard, publicly
   known codes — not something that needed its own data source. Used only
   to join against the `country-region-data` package (MIT licensed) below,
   which keys its regions by this same code rather than by display name —
   that package's own country names are full ISO/formal forms ("Iran,
   Islamic Republic of", "Korea, Republic of") that don't match our
   simpler, curated display names, so joining by code sidesteps having to
   reconcile two different naming schemes. */
export const COUNTRY_ISO = {
  Afghanistan: "AF", Algeria: "DZ", Australia: "AU", Bahrain: "BH",
  Bangladesh: "BD", Belgium: "BE", Brazil: "BR", Brunei: "BN", Canada: "CA",
  China: "CN", Egypt: "EG", France: "FR", Germany: "DE", Ghana: "GH",
  India: "IN", Indonesia: "ID", Iran: "IR", Iraq: "IQ", Ireland: "IE",
  Italy: "IT", Japan: "JP", Jordan: "JO", Kenya: "KE", Kuwait: "KW",
  Lebanon: "LB", Libya: "LY", Malaysia: "MY", Maldives: "MV",
  Mauritius: "MU", Morocco: "MA", Netherlands: "NL", "New Zealand": "NZ",
  Nigeria: "NG", Norway: "NO", Oman: "OM", Pakistan: "PK", Palestine: "PS",
  Philippines: "PH", Qatar: "QA", Russia: "RU", "Saudi Arabia": "SA",
  Senegal: "SN", Singapore: "SG", Somalia: "SO", "South Africa": "ZA",
  Spain: "ES", "Sri Lanka": "LK", Sudan: "SD", Sweden: "SE",
  Switzerland: "CH", Syria: "SY", Tajikistan: "TJ", Tanzania: "TZ",
  Thailand: "TH", Tunisia: "TN", Turkey: "TR", Uganda: "UG", Ukraine: "UA",
  "United Arab Emirates": "AE", "United Kingdom": "GB",
  "United States": "US", Uzbekistan: "UZ", Yemen: "YE",
};

/* Region (state/province) names for a given country from our curated
   COUNTRIES list — [] if the country isn't set yet or the underlying
   dataset has nothing for it (a handful of small/city-states may not),
   in which case the field should just fall back to free text rather than
   showing an empty, unusable dropdown. */
export function getRegionsForCountry(countryName) {
  const iso = COUNTRY_ISO[countryName];
  if (!iso) return [];
  const entry = allCountries.find((c) => c[1] === iso);
  if (!entry) return [];
  return entry[2].map((r) => r[0]).sort();
}
